import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import CommonPageLayout from "../../components/commonPageLayout/commonPageLayout";
import CommonButton from "../../components/CommonButton/CommonButton";
import Loader from "../../components/loader/Loader";
import { Box } from "@mui/material";
import { BUSINESS_DETAILS } from "../../utils/url";
import AccountLoginManagementPage from "../accountloginmanagement/accountLoginManagementPage";
import BusinessDeviceVideoPage from "../businessDeviceVideo/BusinessDeviceVideoPage";
import BusinessGroupPage from "../businessGroup/BusinessGroupPage";
import TabPage from "../../components/tab/tabPage";
import ManageMerchantCardsPage from "../manageMerchantCards/ManageMerchantCardsPage";
import BrandingPage from "../businessBranding/BrandingPage";
import BusinessReportsPage from "../businessReports/BusinessReportsPage";
import DetailsTabPage from "../detailsTab/DetailsTabPage";
import BusinessDevices from "../businessDevices/BusinessDevices";
import BusinessDB from "../businessDB/BusinessDB";
import BusinessProgramPage from "../businessProgram";
import ManageRewards from "../rewards/ManageRewards";
import JoinQRCode from "../businessJoinQRCode/JoinQRCode";
import BusinessEmailPage from "../businessEmail/BusinessEmailPage";

const apiEndpoint = process.env.REACT_APP_API_BASE_URL;

interface BusinessDetails {
  BusinessName: string;
  ResetPasswordTokenUrl: string;
  LoginEmail: string;
  IsLockedOut: number;
  MerchantLoginTokenUrl: string;
  UserId: string;
}

interface MerchantData {
  LegacyMerchantId: string;
  // Add other fields as needed based on the API response
}

const BusinessDetailsPage = () => {
  const { businessId }: any = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get("tab");
  const defaultTabValue = tabParam || "1";
  const [activeTab, setActiveTab] = useState<string>(defaultTabValue);
  const [businessDetails, setBusinessDetails] =
    useState<BusinessDetails | null>(null);
  const [merchantData, setMerchantData] = useState<MerchantData | null>(null);
  const [status, setStatus] = useState<
    "idle" | "fetching" | "success" | "error"
  >("idle");

  const fetchBusinessDetails = async () => {
    if (businessId) {
      try {
        setStatus("fetching");
        const accessToken = localStorage.getItem("accessToken") || "";
        const response = await axios({
          method: "get",
          url: `${apiEndpoint}${BUSINESS_DETAILS}/${businessId}`,
          headers: {
            Authorization: `bearer ${accessToken}`,
            "Content-Type": "application/json"
          }
        });
        setBusinessDetails(response.data);
        setStatus("success");
      } catch (err: any) {
        setStatus("error");
      }
    }
  };

  const fetchMerchantData = async () => {
    if (businessId) {
      try {
        const accessToken = localStorage.getItem("accessToken") || "";
        const response = await axios({
          method: "get",
          url: `${apiEndpoint}/datacollectionbusiness/support/business/${businessId}`,
          headers: {
            Authorization: `bearer ${accessToken}`,
            "Content-Type": "application/json"
          }
        });
        setMerchantData(response.data);
      } catch (err: any) {
        console.error("Failed to fetch merchant data:", err);
      }
    }
  };

  useEffect(() => {
    fetchBusinessDetails();
    fetchMerchantData();
  }, [businessId]);

  // Update active tab when URL query param changes
  useEffect(() => {
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Handle tab change from child components
  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab(newTab);
  }, []);

  const handleRedirectToBusinessSearchPage = () => {
    localStorage.setItem("isBackToBusinessSearchPage", "true");
    navigate("/business-search");
  };

  const tabs = [
    {
      label: "Details",
      value: "1",
      component: <DetailsTabPage />
    },
    {
      label: "Branding",
      value: "2",
      component: businessDetails && <BrandingPage businessId={businessId} />
    },
    {
      label: "Login",
      value: "3",
      component: businessDetails && (
        <AccountLoginManagementPage
          businessId={businessId}
          businessDetails={businessDetails}
          merchantId={merchantData?.LegacyMerchantId}
          onRefresh={fetchBusinessDetails}
        />
      )
    },
    {
      label: "Program",
      value: "4",
      component: businessDetails && <BusinessProgramPage />
    },
    {
      label: "Rewards",
      value: "5",
      component: businessDetails && <ManageRewards />
    },
    {
      label: "QR Code",
      value: "6",
      component: businessDetails && (
        <JoinQRCode onSwitchToRewardsTab={() => handleTabChange("5")} />
      )
    },
    {
      label: "Videos",
      value: "7",
      component: businessDetails && <BusinessDeviceVideoPage />
    },
    {
      label: "Business Group",
      value: "8",
      component: businessDetails && <BusinessGroupPage />
    },
    {
      label: "Cards",
      value: "9",
      component: businessDetails && <ManageMerchantCardsPage />
    },
    {
      label: "Reports",
      value: "10",
      component: businessDetails && (
        <BusinessReportsPage businessId={businessId} />
      )
    },
    {
      label: "Devices",
      value: "11",
      component: businessDetails && <BusinessDevices />
    },
    {
      label: "Device DB",
      value: "12",
      component: businessDetails && <BusinessDB />
    },
    {
      label: "Email",
      value: "13",
      component: businessDetails && (
        <BusinessEmailPage businessId={businessId} />
      )
    }
  ];

  return (
    <>
      <CommonPageLayout
        heading={
          businessDetails?.BusinessName ? businessDetails?.BusinessName : "-"
        }
        subHeading={`Manage Business`}
        docs={
          <CommonButton onClick={handleRedirectToBusinessSearchPage}>
            Back
          </CommonButton>
        }
      >
        {status === "fetching" ? (
          <Loader />
        ) : status === "error" ? (
          <div>{"No Data Available"}</div>
        ) : (
          businessDetails && (
            <TabPage
              tabs={tabs}
              defaultTab={defaultTabValue}
              value={activeTab}
              showRefresh={false}
              onTabChange={handleTabChange}
            />
          )
        )}
      </CommonPageLayout>
    </>
  );
};

export default BusinessDetailsPage;
